import { useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';
import { createStructuredSelector } from 'reselect';

import { FormikProps } from 'formik';

import { Grid2 as Grid, LinearProgress } from '@mui/material';

import { Board } from '@/models/BoardModel.ts';
import { Card } from '@/models/card/CardModel.ts';

import { selectActiveBoard } from '../../ducks/Boards';
import {
  selectPossibleResponsible,
  selectIsLoadingPossibleResponsible,
  selectPossibleResponsibleRequested,
} from '../../ducks/Deliveries';
import { alertAction } from '../../thunks/Alerts';
import * as deliveryActions from '../../thunks/Deliveries';

import {
  getNonMandatoryManualSequenceId,
  getRequiredFields,
  getUpdatedFieldsAndValues,
  hasResponsibleField,
  populateInitialValues,
  UNIQUE_DOC_ID,
} from '../../utils/FieldUtil';
import { withViewModeQuery } from '../../utils/ViewModeUtil';

import PageWrapper from '../../common/PageWrapper';
import Form from '../common/Form';
import DeliveryAttachments from '../common/Attachments';
import DeliveryComments from '../common/Comments';

const mapStateToProps = () =>
  createStructuredSelector({
    board: selectActiveBoard(),
    possibleResponsible: selectPossibleResponsible(),
    isLoadingPossibleResponsible: selectIsLoadingPossibleResponsible(),
    possibleResponsibleRequested: selectPossibleResponsibleRequested(),
  });

const mapDispatchToProps = (dispatch: any) => bindActionCreators({ ...deliveryActions }, dispatch);

const DeliveryCreateContainer = ({
  board,
  possibleResponsible,
  isLoadingPossibleResponsible,
  possibleResponsibleRequested,
  createDelivery,
  fetchPossibleResponsible,
}: {
  board: Board;
  possibleResponsible: any;
  isLoadingPossibleResponsible: boolean;
  possibleResponsibleRequested: { boardId: string };
  createDelivery: any;
  fetchPossibleResponsible: any;
}) => {
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<any>>();
  const [closeOnSuccess, setCloseOnSuccess] = useState(false);
  const { communityId, roomId, boardId } = useParams();
  const hasResponsible = !!hasResponsibleField({ fieldConfig: board.delivery_config });

  useEffect(() => {
    if (hasResponsible && !isLoadingPossibleResponsible && possibleResponsibleRequested.boardId != boardId) {
      fetchPossibleResponsible({ communityId, roomId, boardId });
    }
  }, []);

  const handleSubmit = ({ values, item }: { values: { [id: string]: any }; item: Card }) => {
    const data = {
      type: 'entry',
      fields: getUpdatedFieldsAndValues({ values, item, fieldsConfig: board.delivery_config.fields }),
    };

    return createDelivery({ communityId, roomId, boardId, data }).then((action: any) => {
      alertAction({
        action,
        error: intl.get('delivery.create.error'),
        success: intl.get('delivery.create.success'),
        onSuccess: () =>
          navigate(
            withViewModeQuery(
              closeOnSuccess
                ? `/${communityId}/${roomId}/boards/${boardId}`
                : `/${communityId}/${roomId}/boards/${boardId}/deliveries/${action.payload.delivery.id}`,
            ),
          ),
      });
    });
  };

  const handleCancel = () => navigate(withViewModeQuery(`/${communityId}/${roomId}/boards/${boardId}`));

  const handleSaveClick = (closeForm: boolean) => {
    const form = formRef.current;
    setCloseOnSuccess(closeForm);

    if (form) {
      form.handleSubmit();
    }
  };

  // Create new delivery.
  // TODO: If this is the same as for milestones, re-use the code.
  const delivery: Card = {
    type: 'entry',
    fields: board.delivery_config.fields
      .filter((field) => field.type !== 'auto-number')
      .map((field) => {
        let value = null;
        if (field.type === 'list' || field.type === 'member') {
          if (field.multiple) {
            value = [];
          } else {
            value = '';
          }
        } else if (field.type === 'number') {
          value = 0;
        }
        return {
          id: field.id,
          name: field.name,
          type: field.type,
          value: value,
        };
      }),
    permissions: {},
  };

  if (hasResponsible && (isLoadingPossibleResponsible || possibleResponsibleRequested.boardId != boardId)) {
    return <LinearProgress />;
  }

  return (
    <PageWrapper title={intl.get('app_bar.delivery_card')}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8, xs: 12 }}>
          <Form
            formRef={formRef}
            item={delivery}
            initialValues={populateInitialValues({ item: delivery, fields: board.delivery_config.fields })}
            disabledFields={[UNIQUE_DOC_ID, ...getNonMandatoryManualSequenceId(board.delivery_config.fields)]}
            requiredFields={getRequiredFields({ item: delivery, fields: board.delivery_config.fields })}
            type={board.type}
            fieldConfig={board.delivery_config}
            sequenceNumberDigits={board.workflow_config?.sequence_number_digits}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            restrictions={{
              'task-done': () => ({}),
              'task-responsible': () => ({}),
              'rich-text': () => ({}),
              'auto-number': () => ({}),
              'unique-document-id': () => ({}),
              'sequence-number': () => ({}),
              member: () => ({}),
              list: () => ({}),
              date: (fieldConfig, field) => {
                if (fieldConfig.delivery_met_date_field === field.id) {
                  return { disableFuture: true };
                }
                return {};
              },
              string: () => ({}),
              numeric: () => ({}),
            }}
            possibleResponsible={possibleResponsible}
            submitOptions={[
              {
                title: intl.get('common.form.save_and_close'),
                handleClick: () => handleSaveClick(true),
              },
              {
                title: intl.get('common.form.save'),
                default: true,
                handleClick: () => handleSaveClick(false),
              },
            ]}
          />
          <DeliveryAttachments
            activeCommunity={communityId}
            activeRoom={roomId}
            item={delivery}
            attachments={[]}
            isUploading={false}
            isDeleting={false}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 4, xs: 12 }}>
          <DeliveryComments board={board} item={delivery} comments={[]} members={[]} />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryCreateContainer);
